import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable, type FilterSpec } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { humanDate, messageFor, money, stamp } from "./format";
import { csvName, filterState, isoDay, LOGS_COPY, LOGS_PAGE_SIZE, marketLabel, placeLabel } from "./logs";
import { LogsError, LogsToolbar } from "./LogsToolbar";
import { deviationLabel, exportRrpLog, RRP_COPY, rrpCsv, rrpLogParams, rrpLogQuery, type RrpLogRow } from "./rrp";

const COUNT = new Intl.NumberFormat("en-GB");
const C = RRP_COPY.log;

/** The RRP exceptions pane (Canvas v25 `renderRrpLog`): one server page of the append-only record, newest commit first. */
export function RrpExceptionsPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const params = rrpLogParams(search);
  const page = useQuery(rrpLogQuery(params));
  const heading = <div><h2 className="text-lg font-semibold text-ks-ink">{C.title}</h2><p className="text-sm text-ks-grey-1">{C.note}</p></div>;
  if (page.isPending) return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card></div>;
  if (page.error) return <div className="flex flex-col gap-3">{heading}<LogsError message={messageFor(page.error, areaName)} onRetry={() => void page.refetch()} /></div>;
  const d = page.data;
  const filters = filterState(params, [params.status]);
  if (d.page.total === 0 && filters === "none") {
    return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{C.emptyTitle}</p><p className="text-ks-grey-1">{C.emptyBody}</p></CardContent></Card></div>;
  }
  const paneFilters: FilterSpec[] = [
    { name: "status", label: C.filters.status, kind: "choice", anyLabel: "All", options: d.facets.statuses.map((s) => ({ value: s, label: C.statusLabel[s] })) },
  ];
  const columns: ColumnDef<RrpLogRow, unknown>[] = [
    { id: "committed", header: C.columns[0], cell: ({ row: r }) => <span className="whitespace-nowrap text-sm">{stamp(r.original.committed_at)}</span> },
    { id: "subject", header: C.columns[1], cell: ({ row: r }) => (
      <div><span className="font-semibold text-ks-ink">{r.original.product} {r.original.variant}</span><div className="text-xs text-ks-grey-1">{r.original.xero_sku} · {placeLabel(r.original)}</div></div>
    ) },
    { id: "market", header: C.columns[2], cell: ({ row: r }) => marketLabel(r.original.market) },
    { id: "price", header: C.columns[3], meta: { align: "right" }, cell: ({ row: r }) => <span className="font-semibold tabular-nums text-ks-ink">{money(r.original.price, r.original.currency)}</span> },
    { id: "rrp", header: C.columns[4], meta: { align: "right" }, cell: ({ row: r }) => <RrpCellOf row={r.original} /> },
    { id: "reason", header: C.columns[5], cell: ({ row: r }) => <span className="text-sm">{r.original.reason}</span> },
    { id: "until", header: C.columns[6], cell: ({ row: r }) => (
      <span className="whitespace-nowrap text-sm">{humanDate(r.original.valid_until)}{r.original.ended && <> <Badge variant="outline">{RRP_COPY.ended}</Badge></>}</span>
    ) },
    { id: "who", header: C.columns[7], cell: ({ row: r }) => (
      <div><span className="text-sm">{C.stagedBy(r.original.staged_by)}</span><div className="text-xs text-ks-grey-1">{C.approvedBy(r.original.approver)}</div></div>
    ) },
  ];
  const exportCsv = async () => ({ text: rrpCsv((await exportRrpLog(params)).rows), name: csvName("rrp", isoDay(new Date())) });
  return (
    <div className="flex flex-col gap-3">
      {heading}
      <LogsToolbar areaName={areaName} pane="rrp" search={search} go={go} params={params} facets={d.facets} paneFilters={paneFilters} applied={d.filters}
        total={d.page.total} trailing={C.trailing(COUNT.format(d.page.total))} exportCsv={exportCsv} />
      <DataTable columns={columns} rows={d.rows} page={{ pageIndex: params.page, pageSize: LOGS_PAGE_SIZE, rowCount: d.page.total }}
        onPageChange={(next) => go({ ...search, pp_lpage: next || undefined })} caption={C.title} emptyMessage={filters === "sku" ? LOGS_COPY.noHistorySku : C.noMatch} density="compact" />
      <p className="text-xs text-ks-grey-1">{C.footnote}</p>
    </div>
  );
}

function RrpCellOf({ row }: { row: RrpLogRow }) {
  return (
    <div>
      <span className="tabular-nums">{money(row.rrp, row.rrp_currency)}</span>
      <div className="text-xs text-ks-grey-1">{C.anchor(row.anchor_market_name ?? row.anchor_market)}</div>
      {row.deviation_pct != null && <Badge variant="outline" className="border-ks-warn text-ks-warn">{deviationLabel(row.deviation_pct)}</Badge>}
    </div>
  );
}
